import type {
	EpisodePlan,
	FlagCondition,
	FlagEffect,
	PlanChoice,
	PlanDecision,
	PlanEnding,
	PlanEndingRule,
	PlanNode,
	PlanShot,
	StoryFlagDef,
} from "@storyline/shared";

const replaceAt = <T>(items: T[], index: number, next: T): T[] => items.map((item, position) => (position === index ? next : item));

export const setPlanField = (
	plan: EpisodePlan,
	patch: Partial<Pick<EpisodePlan, "title" | "synopsis" | "entryNodeIndex">>,
): EpisodePlan => ({
	...plan,
	...patch,
});

export const updateNode = (plan: EpisodePlan, nodeIndex: number, patch: Partial<PlanNode>): EpisodePlan => ({
	...plan,
	nodes: replaceAt(plan.nodes, nodeIndex, { ...plan.nodes[nodeIndex], ...patch }),
});

export const updateShot = (plan: EpisodePlan, nodeIndex: number, shotIndex: number, patch: Partial<PlanShot>): EpisodePlan => {
	const node = plan.nodes[nodeIndex];
	return updateNode(plan, nodeIndex, { shots: replaceAt(node.shots, shotIndex, { ...node.shots[shotIndex], ...patch }) });
};

export const updateDecision = (plan: EpisodePlan, nodeIndex: number, patch: Partial<PlanDecision>): EpisodePlan => {
	const decision = plan.nodes[nodeIndex].decision;
	if (!decision) {
		return plan;
	}
	return updateNode(plan, nodeIndex, { decision: { ...decision, ...patch } });
};

export const updateChoice = (plan: EpisodePlan, nodeIndex: number, choiceIndex: number, patch: Partial<PlanChoice>): EpisodePlan => {
	const decision = plan.nodes[nodeIndex].decision;
	if (!decision) {
		return plan;
	}
	return updateDecision(plan, nodeIndex, {
		choices: replaceAt(decision.choices, choiceIndex, { ...decision.choices[choiceIndex], ...patch }),
	});
};

export const updateEnding = (plan: EpisodePlan, endingIndex: number, patch: Partial<PlanEnding>): EpisodePlan => ({
	...plan,
	endings: replaceAt(plan.endings, endingIndex, { ...plan.endings[endingIndex], ...patch }),
});

export const updateRule = (plan: EpisodePlan, ruleIndex: number, patch: Partial<PlanEndingRule>): EpisodePlan => ({
	...plan,
	endingRules: replaceAt(plan.endingRules, ruleIndex, { ...plan.endingRules[ruleIndex], ...patch }),
});

export const addRule = (plan: EpisodePlan, rule: PlanEndingRule): EpisodePlan => ({ ...plan, endingRules: [...plan.endingRules, rule] });

export const removeRule = (plan: EpisodePlan, ruleIndex: number): EpisodePlan => ({
	...plan,
	endingRules: plan.endingRules.filter((_, position) => position !== ruleIndex),
});

export const addFlag = (plan: EpisodePlan, flag: StoryFlagDef): EpisodePlan => ({ ...plan, flags: [...plan.flags, flag] });

export const removeFlag = (plan: EpisodePlan, flagIndex: number): EpisodePlan => ({
	...plan,
	flags: plan.flags.filter((_, position) => position !== flagIndex),
});

export const setChoiceEffects = (plan: EpisodePlan, nodeIndex: number, choiceIndex: number, effects: FlagEffect[]): EpisodePlan =>
	updateChoice(plan, nodeIndex, choiceIndex, { effects });

export const setRuleConditions = (plan: EpisodePlan, ruleIndex: number, conditions: FlagCondition[]): EpisodePlan =>
	updateRule(plan, ruleIndex, { conditions });
